/**
 * @author      yontaek@zumper.com (Yon Taek Chung)
 * @copyright   Copyright (c) 2023, Zumper
 * @description PinMarker
 */

import { Marker } from '@vis.gl/react-mapbox'
import { Popover, PopoverContent, PopoverTrigger } from '@zcode-ui/shadz-ui'
import React, { Fragment } from 'react'
import type { PinMarkerDesktopProps } from './PinMarkerDesktop'
import { PinMarkerGroupPreview } from './PinMarkerGroupPreview/PinMarkerGroupPreview'
import { PinMarkerListingPreview } from './PinMarkerListingPreview'
import { PinWithInfo } from './PinWithInfo'

type PinMarkerMobileProps = Omit<PinMarkerDesktopProps, 'hovered'>

export const PinMarkerMobile = ({
  handlePinClick,
  isActive,
  isDot,
  isSpotlight,
  isVisited,
  lat,
  lng,
  markerRef,
  mixpanelMaplistClick,
  onClose,
  onMouseOut,
  onMouseOver,
  previewListable,
  priceTxt,
}: PinMarkerMobileProps) => {
  const isGroup = Array.isArray(previewListable)
  if (!lat || !lng) {
    return null
  }

  return (
    <Fragment>
      <Marker
        anchor={isDot ? undefined : 'bottom'}
        draggable={false}
        latitude={lat}
        longitude={lng}
        style={{
          zIndex: isActive ? 4 : isSpotlight ? 3 : !isDot ? 1 : 'unset',
          cursor: 'pointer',
        }}
      >
        <div onMouseOut={onMouseOut} onMouseOver={onMouseOver} ref={markerRef}>
          <div onClick={handlePinClick} role="presentation">
            {isDot ? (
              <div
                className={`h-[14px] w-[14px] cursor-pointer rounded-full border border-solid ${
                  isActive
                    ? 'bg-gray-0 border-pink-600 border-2'
                    : isVisited
                      ? 'bg-neutral-650 border-gray-0'
                      : 'bg-blue-800 border-gray-0'
                }`}
              />
            ) : (
              <PinWithInfo
                isActive={isActive}
                isSpotlight={isSpotlight}
                isVisited={isVisited}
                priceTxt={priceTxt}
              />
            )}
          </div>
        </div>
      </Marker>

      <div className="fixed bottom-[78px] right-1/2 z-[4]">
        <Popover
          open={isActive}
          onOpenChange={(open) => {
            !open && onClose()
          }}
        >
          <PopoverTrigger>
            <div />
          </PopoverTrigger>

          {isActive && (
            <PopoverContent
              className={`border-none mx-6 p-0 rounded-lg overflow-hidden focus:outline-none focus:ring-0 ${isGroup ? 'h-[168px] shadow-[0px_8px_16px_rgba(0,0,0,0.12)] rounded-lg mx-3' : 'h-[116px] shadow-none'}`}
              onOpenAutoFocus={(e) => e.preventDefault()}
              side="top"
              sideOffset={8}
              style={{ width: isGroup ? 'calc(100vw - 28px)' : 'calc(100vw - 48px)' }}
            >
              {isGroup ? (
                <PinMarkerGroupPreview
                  isMobile
                  mixpanelMaplistClick={mixpanelMaplistClick}
                  onClose={onClose}
                  previewListable={previewListable}
                />
              ) : (
                <PinMarkerListingPreview
                  listable={previewListable.pin}
                  mixpanelMaplistClick={mixpanelMaplistClick}
                  onClose={onClose}
                />
              )}
            </PopoverContent>
          )}
        </Popover>
      </div>
    </Fragment>
  )
}
